import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Wrench, Check, Plus, X, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Section, SectionTitle } from "./Section";
import { formatMoney } from "@/lib/pricing";

export function SpecialEffectSection({ effectMode, onChange, settings, sym }) {
  const fx = settings.special_effect;
  return (
    <Section>
      <SectionTitle n="3" en="Tricoat Paint Upgrade" zh="三层漆升级" icon={Sparkles} />
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-sm text-slate-400 max-w-md">Enable the <span className="text-violet-300 font-semibold">Tricoat</span> toggle on any panel above (pearl, candy, chameleon, matte). Choose how the upgrade is calculated:</p>
        <div className="flex rounded-xl border border-slate-700 bg-slate-950/60 p-1 shrink-0">
          <button data-on={effectMode === "fixed"} onClick={() => onChange("fixed")} data-testid="special-effect-mode-fixed"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-400 data-[on=true]:bg-violet-500 data-[on=true]:text-white transition-colors">
            Fixed {formatMoney(sym, fx.fixed_rate_per_panel)}
          </button>
          <button data-on={effectMode === "percent"} onClick={() => onChange("percent")} data-testid="special-effect-mode-percent"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-400 data-[on=true]:bg-violet-500 data-[on=true]:text-white transition-colors">
            Percent {fx.percentage_rate}%
          </button>
        </div>
      </div>
    </Section>
  );
}

function ServiceCard({ s, value, sym, onToggle, onPrice }) {
  const on = value !== undefined;
  return (
    <div data-on={on} data-testid={`service-addon-${s.id}`}
      className="rounded-xl border-2 border-slate-700 bg-slate-900/50 transition-all hover:border-slate-500 data-[on=true]:border-emerald-400 data-[on=true]:bg-emerald-500/10">
      <button onClick={() => onToggle(s.id, s.price)} data-testid={`service-addon-toggle-${s.id}`} className="w-full flex items-center justify-between gap-3 p-3.5 text-left active:scale-[0.99]">
        <div>
          <div className="font-semibold text-sm">{s.en}</div>
          <div className="font-zh text-xs text-amber-300/70">{s.zh}</div>
        </div>
        <div className="flex items-center gap-2">
          {!on && <span className="text-xs text-slate-500">{Number(s.price) > 0 ? formatMoney(sym, s.price) : "Custom price"}</span>}
          <span className={`w-5 h-5 rounded-md grid place-items-center border ${on ? "bg-emerald-400 border-emerald-400 text-slate-950" : "border-slate-600"}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
        </div>
      </button>
      {on && (
        <div className="flex items-center justify-between gap-3 px-3.5 pb-3.5">
          <span className="text-xs uppercase tracking-wider text-emerald-300/80 font-semibold">Price 价格</span>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{sym}</span>
            <Input type="number" min="0" step="1" autoFocus value={value} placeholder="0" onChange={(e) => onPrice(s.id, e.target.value)}
              data-testid={`service-price-${s.id}`} className="bg-slate-950/60 border-slate-700 h-8 w-28 pl-6 text-right font-mono-num" />
          </div>
        </div>
      )}
    </div>
  );
}

function CustomServiceForm({ onAdd }) {
  const [form, setForm] = useState({ en: "", price: "" });
  const submit = () => {
    if (!form.en.trim()) return toast.error("Enter a job name");
    onAdd({ en: form.en.trim(), price: form.price });
    setForm({ en: "", price: "" });
  };
  return (
    <div className="rounded-xl border border-dashed border-slate-700 bg-slate-950/30 p-3.5 space-y-2" data-testid="custom-service-form">
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Ad-hoc job · 其他项目</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="Job name (e.g. Motorbike tank respray)" value={form.en} onChange={(e) => setForm({ ...form, en: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="custom-service-name-input" className="bg-slate-950/60 border-slate-700 h-9 text-sm" />
        <Input placeholder="Price" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="custom-service-price-input" className="bg-slate-950/60 border-slate-700 h-9 text-sm font-mono-num sm:w-36 placeholder:text-xs" />
        <Button onClick={submit} data-testid="custom-service-add-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-9 font-bold shrink-0"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function CustomServiceCard({ s, sym, onRemove }) {
  return (
    <div className="rounded-xl border-2 border-emerald-400 bg-emerald-500/10 p-3.5 flex items-center justify-between gap-3" data-testid={`custom-service-${s.id}`}>
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate">{s.en}</div>
        <div className="text-xs text-emerald-300/80">Ad-hoc job · 其他项目</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono-num text-sm font-semibold">{formatMoney(sym, s.price)}</span>
        <button onClick={onRemove} data-testid={`custom-service-remove-${s.id}`} title="Remove this job" className="w-7 h-7 grid place-items-center rounded-md bg-red-600 text-white shadow-sm shadow-red-900/40 hover:bg-red-500 active:scale-95 transition-colors"><X className="w-4 h-4" strokeWidth={3} /></button>
      </div>
    </div>
  );
}

export function ServicesSection({ settings, services, onToggle, onPrice, sym, customServices = [], onAddCustom, onRemoveCustom }) {
  return (
    <Section>
      <SectionTitle n="4" en="Add-on Services" zh="附加服务" icon={Wrench} />
      <p className="text-xs text-slate-500 mt-1">Tap a service, then enter the price for this job. Use "Ad-hoc job" for anything not on the list — even non-car work.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {settings.services.map((s) => <ServiceCard key={s.id} s={s} value={services[s.id]} sym={sym} onToggle={onToggle} onPrice={onPrice} />)}
        {customServices.map((s) => <CustomServiceCard key={s.id} s={s} sym={sym} onRemove={() => onRemoveCustom(s.id)} />)}
      </div>
      <div className="mt-3"><CustomServiceForm onAdd={onAddCustom} /></div>
    </Section>
  );
}

function PartRow({ p, sym, onChange, onRemove }) {
  const total = (Number(p.price) || 0) + (Number(p.ri) || 0);
  const inp = "bg-slate-950/60 border-slate-700 h-8 text-sm font-mono-num text-right pl-6";
  return (
    <div className="rounded-xl border-2 border-sky-500/50 bg-sky-500/5 p-3 grid grid-cols-2 sm:grid-cols-[1fr_120px_120px_90px_32px] gap-2 items-center" data-testid={`part-row-${p.id}`}>
      <div className="col-span-2 sm:col-span-1 min-w-0">
        <div className="font-semibold text-sm truncate">{p.name}</div>
        <div className="text-[11px] text-sky-300/80">Spare part · 零件</div>
      </div>
      <label className="relative block">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">{sym}</span>
        <Input type="number" min="0" value={p.price} onChange={(e) => onChange({ price: e.target.value })} data-testid={`part-price-${p.id}`} className={inp} title="Part price" />
        <span className="block text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Part 零件</span>
      </label>
      <label className="relative block">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">{sym}</span>
        <Input type="number" min="0" value={p.ri} onChange={(e) => onChange({ ri: e.target.value })} data-testid={`part-ri-${p.id}`} className={inp} title="Remove & install labour" />
        <span className="block text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">R&amp;I 拆装</span>
      </label>
      <div className="font-mono-num font-bold text-sm text-right sm:self-start sm:pt-1.5" data-testid={`part-total-${p.id}`}>{formatMoney(sym, total)}</div>
      <button onClick={onRemove} data-testid={`part-remove-${p.id}`} title="Remove this part" className="w-8 h-8 grid place-items-center rounded-md bg-red-600 text-white shadow-sm shadow-red-900/40 hover:bg-red-500 active:scale-95 transition-colors justify-self-end sm:self-start"><X className="w-4 h-4" strokeWidth={3} /></button>
    </div>
  );
}

function PartForm({ onAdd }) {
  const [form, setForm] = useState({ name: "", price: "", ri: "" });
  const submit = () => {
    if (!form.name.trim()) return toast.error("Enter a part name");
    onAdd({ name: form.name.trim(), price: form.price, ri: form.ri });
    setForm({ name: "", price: "", ri: "" });
  };
  const onKey = (e) => e.key === "Enter" && submit();
  return (
    <div className="rounded-xl border border-dashed border-slate-700 bg-slate-950/30 p-3.5 space-y-2" data-testid="part-form">
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add part · 添加零件</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="Part name (e.g. Front bumper, OEM)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={onKey} data-testid="part-name-input" className="bg-slate-950/60 border-slate-700 h-9 text-sm" />
        <Input placeholder="Part price" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} onKeyDown={onKey} data-testid="part-price-input" className="bg-slate-950/60 border-slate-700 h-9 text-sm font-mono-num sm:w-36 placeholder:text-xs" />
        <Input placeholder="R&I labour" type="number" min="0" value={form.ri} onChange={(e) => setForm({ ...form, ri: e.target.value })} onKeyDown={onKey} data-testid="part-ri-input" className="bg-slate-950/60 border-slate-700 h-9 text-sm font-mono-num sm:w-36 placeholder:text-xs" />
        <Button onClick={submit} data-testid="part-add-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 h-9 font-bold shrink-0"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export function PartsSection({ parts, sym, onAdd, onUpdate, onRemove }) {
  return (
    <Section>
      <SectionTitle n="5" en="Spare Parts" zh="更换零件" icon={Package} />
      <p className="text-xs text-slate-500 mt-1">Parts the customer needs replaced. <span className="text-sky-300">Net pricing</span> — discounts never apply to parts. R&amp;I = remove &amp; install labour (optional).</p>
      <div className="space-y-2 mt-4">
        {parts.map((p) => <PartRow key={p.id} p={p} sym={sym} onChange={(patch) => onUpdate(p.id, patch)} onRemove={() => onRemove(p.id)} />)}
        <PartForm onAdd={onAdd} />
      </div>
    </Section>
  );
}
